import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    storage: typeof window !== 'undefined' ? window.localStorage : undefined,
  }
});

export interface Product {
  id?: string;
  name: string;
  price: string;
  link?: string;
  trendyol_link?: string;
  image_url?: string;
}

export interface Video {
  id: string;
  title: string;
  description: string;
  video_url: string;
  thumbnail_url: string;
  products: Product[];
  tags: string[];
  main_category_id: string;
  sub_category_id: string;
  brand_id: string;
  created_at: string;
  view_count: number;
  like_count: number;
}

// Helper function to parse JSON fields that might be strings or arrays
function parseJsonField<T>(field: any, fieldName: string, videoId?: string): T[] {
  // Already an array
  if (Array.isArray(field)) {
    return field;
  }
  
  // Null or undefined
  if (!field || field === null || field === undefined) {
    return [];
  }
  
  // String - try to parse
  if (typeof field === 'string') {
    const trimmed = field.trim();
    
    // Empty or null-like strings
    if (trimmed === '' || trimmed === '[]' || trimmed === '{}' || trimmed === 'null') {
      return [];
    }
    
    try {
      const parsed = JSON.parse(trimmed);
      
      // Parsed to array
      if (Array.isArray(parsed)) {
        console.log(`✅ [Video ${videoId}] Parsed ${fieldName} from string: ${parsed.length} items`);
        return parsed;
      }
      
      // Parsed to object - wrap in array
      if (parsed && typeof parsed === 'object') {
        console.log(`✅ [Video ${videoId}] Parsed ${fieldName} from string (object): 1 item`);
        return [parsed];
      }
      
      console.warn(`⚠️ [Video ${videoId}] Unexpected ${fieldName} type after parsing:`, typeof parsed);
      return [];
    } catch (e) {
      console.error(`❌ [Video ${videoId}] Failed to parse ${fieldName}:`, e);
      console.error(`❌ Raw data:`, trimmed.substring(0, 100));
      return [];
    }
  }
  
  // Single object - wrap in array
  if (typeof field === 'object') {
    console.log(`✅ [Video ${videoId}] ${fieldName} is object, wrapping in array`);
    return [field];
  }
  
  console.warn(`⚠️ [Video ${videoId}] Unknown ${fieldName} type:`, typeof field);
  return [];
}

interface VideoCreateInput {
  title: string;
  description?: string;
  video_url: string;
  thumbnail_url?: string;
  products: Product[];
  tags: string[];
}

interface VideoUpdateInput {
  title?: string;
  description?: string;
  video_url?: string;
  thumbnail_url?: string;
  products?: Product[];
  tags?: string[];
  like_count?: number;
  views_count?: number;
}

// Video database operations
export const videoService = {
  // Get all videos
  async getVideos(): Promise<Video[]> {
    const { data, error } = await supabase
      .from('videos')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error('Supabase error:', error);
      throw error;
    }
    
    // Transform data to ensure compatibility and provide defaults
    return (data || []).map(video => {
      // Parse products field - handle both string and array formats
      let products: Product[] = [];
      if (video.products) {
        if (typeof video.products === 'string') {
          try {
            products = JSON.parse(video.products);
          } catch (e) {
            console.error('Failed to parse products for video:', video.id, e);
            products = [];
          }
        } else if (Array.isArray(video.products)) {
          products = video.products;
        }
      }

      // Parse tags field - handle both string and array formats
      let tags: string[] = [];
      if (video.tags) {
        if (typeof video.tags === 'string') {
          try {
            tags = JSON.parse(video.tags);
          } catch (e) {
            console.error('Failed to parse tags for video:', video.id, e);
            tags = [];
          }
        } else if (Array.isArray(video.tags)) {
          tags = video.tags;
        }
      }

      return {
        ...video,
        description: video.description || '',
        thumbnail: video.thumbnail_url || video.thumbnail || '',
        thumbnail_url: video.thumbnail_url || '',
        products,
        tags,
        slug: video.slug || video.id,
        updated_at: video.updated_at || video.created_at || new Date().toISOString(),
        like_count: video.like_count || 0,
        views_count: video.views_count || 0
      };
    });
  },

  // Get single video by ID only (no slug support since column doesn't exist)
  async getVideo(id: string): Promise<Video | null> {
    const { data, error } = await supabase
      .from('videos')
      .select('*')
      .eq('id', id)
      .single();
    
    if (error) {
      if (error.code === 'PGRST116') {
        // No rows returned
        return null;
      }
      console.error('Supabase error:', error);
      throw error;
    }
    
    if (!data) return null;
    
    // Parse products field - handle both string and array formats
    let products: Product[] = [];
    if (data.products) {
      if (typeof data.products === 'string') {
        try {
          products = JSON.parse(data.products);
        } catch (e) {
          console.error('Failed to parse products for video:', data.id, e);
          products = [];
        }
      } else if (Array.isArray(data.products)) {
        products = data.products;
      }
    }

    // Parse tags field - handle both string and array formats
    let tags: string[] = [];
    if (data.tags) {
      if (typeof data.tags === 'string') {
        try {
          tags = JSON.parse(data.tags);
        } catch (e) {
          console.error('Failed to parse tags for video:', data.id, e);
          tags = [];
        }
      } else if (Array.isArray(data.tags)) {
        tags = data.tags;
      }
    }

    // Transform data to ensure compatibility and provide defaults
    return {
      ...data,
      description: data.description || '',
      thumbnail: data.thumbnail_url || data.thumbnail || '',
      thumbnail_url: data.thumbnail_url || '',
      products,
      tags,
      slug: data.slug || data.id,
      updated_at: data.updated_at || data.created_at || new Date().toISOString(),
      like_count: data.like_count || 0,
      views_count: data.views_count || 0
    };
  },

  // Create new video
  async createVideo(video: VideoCreateInput): Promise<Video> {
    const { data, error } = await supabase
      .from('videos')
      .insert([video])
      .select()
      .single();
    
    if (error) throw error;
    if (!data) throw new Error('Failed to create video');
    
    return {
      ...data,
      description: data.description || '',
      thumbnail: data.thumbnail_url || '',
      thumbnail_url: data.thumbnail_url || '',
      products: Array.isArray(data.products) ? data.products : [],
      tags: Array.isArray(data.tags) ? data.tags : [],
      slug: data.slug || data.id,
      updated_at: data.updated_at || data.created_at || new Date().toISOString(),
      like_count: data.like_count || 0,
      views_count: data.views_count || 0
    };
  },

  // Update video
  async updateVideo(id: string, updates: VideoUpdateInput): Promise<Video> {
    const { data, error } = await supabase
      .from('videos')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    if (!data) throw new Error('Failed to update video');
    
    return {
      ...data,
      description: data.description || '',
      thumbnail: data.thumbnail_url || '',
      thumbnail_url: data.thumbnail_url || '',
      products: Array.isArray(data.products) ? data.products : [],
      tags: Array.isArray(data.tags) ? data.tags : [],
      slug: data.slug || data.id,
      updated_at: data.updated_at || data.created_at || new Date().toISOString(),
      like_count: data.like_count || 0,
      views_count: data.views_count || 0
    };
  },

  // Delete video
  async deleteVideo(id: string): Promise<void> {
    const { error } = await supabase
      .from('videos')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  },

  // Upload video file to Supabase Storage
  async uploadVideo(file: File): Promise<string> {
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}.${fileExt}`;
    const filePath = `videos/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('videos')
      .upload(filePath, file);

    if (uploadError) throw uploadError;

    const { data } = supabase.storage
      .from('videos')
      .getPublicUrl(filePath);

    return data.publicUrl;
  }
};
